import { PackageProduct } from "../model";
import { getLocalApi } from "./localApi";
import apiConfig from "app/api/apiConfig.json";
import { productTypeKeyToId } from "./mappers/mapPackageProduct";
import dayjs from "dayjs";
import { ApiErrors } from "./ApiErrors";

interface SavePackageResult {
    ok: boolean;
    message: string;
    id?: number;
    productId?: number;
    errors?: string[];
}

export async function savePackage(product: PackageProduct, divisionId: number): Promise<SavePackageResult> {
    const localApi = await getLocalApi();

    const payload: Record<string, any> = {
        name: product.name,
        description: product.description ?? '',
        divisionId,
        minimumAge: product.minimumAge ?? null,
        maximumAge: product.maximumAge ?? null,
        minimumWeeks: product.minimumWeeks ?? null,
        accountCategoryId: product.accountCategoryId ?? null,
        productCategoryId: product.productCategoryId ?? null,
        active: product.active ?? true,
        packageBreakdowns: (product.items ?? []).map(item => ({
            productId: item.productId,
            productTypeId: item.productTypeId ?? productTypeKeyToId[item.productType ?? 'Course'],
            percentageBreakdown: Number((item.percentageBreakdown ?? 0).toFixed(1)),
        })),
    };

    const isUpdate = !!(product.id != null && Number(product.id) > 0);
    if (isUpdate) {
        payload.id = product.id;
    }
    const endpoint = isUpdate
        ? apiConfig.packages_update.replace("{packageId}", String(product.id))
        : apiConfig.packages_save;
    const method = isUpdate ? "PUT" : "POST";

    const response = await localApi.call(
        endpoint,
        method,
        JSON.stringify(payload)
    );

    if (!response) {
        return { ok: false, message: "No response from API" };
    }

    let rawText = "";
    try {
        rawText = await response.text();
    } catch {
        rawText = "";
    }

    let parsed: any = null;
    if (rawText) {
        try {
            parsed = JSON.parse(rawText);
        } catch {
            parsed = rawText;
        }
    }

    const errors = ApiErrors.extractAllErrors(parsed, response, "Failed to save package");
    const message = response.ok
        ? (parsed?.message || "Package saved successfully")
        : ApiErrors.formatErrors(errors);

    const productId = response.ok
        ? (typeof parsed === "number"
            ? parsed
            : typeof parsed?.productId === "number"
                ? parsed.productId
                : typeof parsed?.id === "number"
                    ? parsed.id
                    : parsed?.data?.productId ?? parsed?.data?.id ?? (isUpdate ? product.id : undefined))
        : undefined;

    return { ok: response.ok, message, id: productId, productId, errors: response.ok ? undefined : errors };
}

function toApiDate(value?: Date | null): string | null {
    if (!value) {
        return null;
    }

    const date = dayjs(value);
    if (!date.isValid()) {
        return null;
    }

    return date.format('YYYY-MM-DD');
}